import { useState } from 'react'
import { View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { InlineAlert } from '@chillout/ui'
import { StampPreview } from '../../shared/StampPreview'
import { AdjustmentForm } from './AdjustmentForm'
import { StampAdjustConfirmModal } from './StampAdjustConfirmModal'
import { useAdminAdjustStamps } from './useAdminAdjustMember'
import type { AdminMemberDetail } from './useAdminMemberDetail'

interface StampsTabContentProps {
  detail: AdminMemberDetail
}

function getCountLabel(delta: number, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const abs = Math.abs(delta)
  const label = abs === 1
    ? t('members.detail.stampAdjustment.stampCountSingle')
    : t('members.detail.stampAdjustment.stampCount', { count: abs })
  if (delta > 0) return `+ ${label}`
  if (delta < 0) return `- ${label}`
  return label
}

function getFullName(firstName: string | null, lastName: string | null): string {
  return [firstName, lastName].filter(Boolean).join(' ') || '—'
}

export function StampsTabContent({ detail }: StampsTabContentProps) {
  const { t } = useTranslation()
  const [stampDelta, setStampDelta] = useState(0)
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')
  const [showConfirm, setShowConfirm] = useState(false)

  const adjustMutation = useAdminAdjustStamps()

  const { currentStamps, stampTarget, completionCardIcon, milestones } = detail
  const canApply = stampDelta !== 0 && reason !== ''

  function handleConfirm() {
    adjustMutation.mutate(
      {
        memberId: detail.id,
        stampsDelta: stampDelta,
        correctionReason: reason,
        correctionNote: note,
      },
      {
        onSuccess: () => {
          setShowConfirm(false)
          setStampDelta(0)
          setReason('')
          setNote('')
        },
      },
    )
  }

  function handleOpenConfirm() {
    adjustMutation.reset()
    setShowConfirm(true)
  }

  return (
    <View className="gap-6">
      <StampPreview
        currentStamps={currentStamps}
        stampDelta={stampDelta}
        stampTarget={stampTarget}
        completionCardIcon={completionCardIcon}
        milestones={milestones}
        title={t('members.detail.stampPreview.title')}
        stampSize={40}
        justify="flex-start"
      />

      <AdjustmentForm
        delta={stampDelta}
        onDeltaChange={setStampDelta}
        min={-currentStamps}
        max={detail.maxStampsPerTransaction}
        reason={reason}
        onReasonChange={setReason}
        note={note}
        onNoteChange={setNote}
        canApply={canApply}
        onApply={handleOpenConfirm}
        stepperLabel={t('members.detail.stampAdjustment.numberOfStamps')}
        countLabel={getCountLabel(stampDelta, t)}
        decrementAccessibilityLabel={t('members.detail.stampAdjustment.decrementAccessibilityLabel')}
        incrementAccessibilityLabel={t('members.detail.stampAdjustment.incrementAccessibilityLabel')}
        countAccessibilityLabel={t('members.detail.stampAdjustment.countAccessibilityLabel', { count: stampDelta })}
        reasonLabel={t('members.detail.stampAdjustment.reason')}
        reasonPlaceholder={t('members.detail.stampAdjustment.reasonPlaceholder')}
        noteLabel={t('members.detail.stampAdjustment.note')}
        notePlaceholder={t('members.detail.stampAdjustment.notePlaceholder')}
        applyLabel={t('members.detail.stampAdjustment.applyButton')}
      />

      {adjustMutation.isError ? (
        <InlineAlert message={t('members.detail.stampAdjustment.applyError')} />
      ) : null}

      <StampAdjustConfirmModal
        visible={showConfirm}
        memberName={getFullName(detail.firstName, detail.lastName)}
        memberEmail={detail.email}
        currentStamps={currentStamps}
        stampDelta={stampDelta}
        stampTarget={stampTarget}
        reason={reason}
        note={note}
        onConfirm={handleConfirm}
        onCancel={() => setShowConfirm(false)}
      />
    </View>
  )
}